import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { useToast } from "@/hooks/useToast";
import { LogIn, RefreshCw } from "lucide-react";
import { verifyOtp, requestOtp } from "@/api/auth";
import { useAuth } from "@/contexts/AuthContext";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import appLogo from "@/images/notanaicon.svg";

export function Verify() {
  // State to track the OTP input fields
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  // State to track loading status during OTP verification
  const [loading, setLoading] = useState(false);
  // State to track loading status during OTP resend
  const [resending, setResending] = useState(false);
  // State to track any error messages
  const [error, setError] = useState<string | null>(null);
  // Access toast notifications for user feedback
  const { toast } = useToast();
  // Hook to navigate to different routes
  const navigate = useNavigate();
  // Access email passed via route state
  const location = useLocation();
  const email = location.state?.email;
  // Access the login function from AuthContext
  const { login } = useAuth();
  // Reference to focus the first OTP input field
  const firstInputRef = useRef<HTMLInputElement>(null);

  // Effect to focus the first OTP input field on component mount
  useEffect(() => {
    if (!email) {
      console.log("No email found in location state, redirecting to login.");
      navigate("/login", { replace: true });
      return;
    }

    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [email, navigate]);

  // Function to handle OTP verification
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Join the OTP array into a single string
    const otpValue = otp.join("");
    console.log("Entered OTP:", otpValue);

    // Validate that the OTP is complete (6 digits)
    if (otpValue.length !== 6) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter all 6 digits",
      });
      console.log("OTP validation failed: Incomplete OTP");
      return;
    }

    console.log("Attempting to verify OTP for email:", email);

    try {
      setLoading(true);
      console.log("Verifying OTP:", otpValue);
      // Call the API to verify the OTP
      const response = await verifyOtp(email, otpValue);
      console.log("API response:", response);

      // If verification is successful
      if (response.success) {
        console.log("OTP verification successful");
        
        // Update auth context
        login();
        console.log("Auth context updated, user logged in");
        
        // Delay navigation slightly to ensure auth context is updated
        setTimeout(() => {
          console.log("Navigating to meetings page");
          navigate("/app/meetings", { replace: true });
        }, 500);
      } else {
        console.log("OTP verification failed:", response.message || "No message");
        setError(response.message || "Verification failed");
      }
    } catch (error: any) {
      console.error("Error during OTP verification:", error);
      // Set error state for display
      setError(error.response?.data?.message || "Invalid or expired verification code");

      // Clear the OTP fields and refocus the first input
      setOtp(["", "", "", "", "", ""]);
      if (firstInputRef.current) {
        firstInputRef.current.focus();
      }
    } finally {
      setLoading(false);
      console.log("Finished OTP verification attempt");
    }
  };

  // Function to handle resending the OTP
  const handleResendCode = async () => {
    console.log("Attempting to resend OTP for email:", email);

    try {
      setResending(true);
      setError(null);
      // Call the API to request a new OTP
      await requestOtp(email);
      console.log("OTP resent successfully");

      // Clear the OTP fields and refocus the first input
      setOtp(["", "", "", "", "", ""]);
      if (firstInputRef.current) {
        firstInputRef.current.focus();
      }
    } catch (error) {
      console.error("Error while resending OTP:", error);
      // Show error toast
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to resend verification code",
      });
    } finally {
      setResending(false);
      console.log("Finished OTP resend attempt");
    }
  };

  // Function to handle OTP input field changes
  const handleInputChange = (index: number, value: string) => {
    const newOtp = [...otp];
    newOtp[index] = value.slice(0, 1);
    setOtp(newOtp);

    // Auto-focus next input if the current input has a value
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  // Function to handle backspace and auto-focus previous input
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
      }
    }
  };

  // Function to handle pasting OTP into the input fields
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");
    const digits = pastedData.replace(/\D/g, "").slice(0, 6).split("");

    if (digits.length === 6) {
      setOtp(digits);
      // Focus the last input after pasting
      const lastInput = document.getElementById("otp-5");
      if (lastInput) {
        lastInput.focus();
      }
    }
  };

  // If no email is present, render nothing (user is redirected)
  if (!email) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden p-4">
      {/* Animated Background Gradient Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Base gradient layer */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-slate-100 to-indigo-50 dark:from-slate-900 dark:via-slate-800 dark:to-indigo-950"></div>
        
        <div className="absolute top-0 -left-40 w-96 h-96 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-20 dark:opacity-30 animate-blob" />
        <div className="absolute top-0 -right-40 w-96 h-96 bg-gradient-to-r from-indigo-400 to-violet-400 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-20 dark:opacity-30 animate-blob animation-delay-2000" />
        <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 w-96 h-96 bg-gradient-to-r from-violet-400 to-purple-400 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-20 dark:opacity-30 animate-blob animation-delay-4000" />
      </div>

      {/* Grain Texture Overlay */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxwYXRoIGQ9Ik0wIDBoMzAwdjMwMEgweiIgZmlsdGVyPSJ1cmwoI2EpIiBvcGFjaXR5PSIuMDUiLz48L3N2Zz4=')] opacity-40 dark:opacity-20 pointer-events-none" />

      <Card className="w-full max-w-md relative z-10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <img src={appLogo} alt="Notana Logo" className="h-8 w-8 dark:invert" />
            <span className="bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">Verify your email</span>
          </CardTitle>
          <CardDescription>
            Enter the 6-digit code sent to {email}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleVerifyOtp} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription className="flex flex-col gap-2">
                  <span>{error}</span>
                </AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label>Enter verification code</Label>
              <div className="flex gap-2 items-center justify-between" onPaste={handlePaste}>
                {otp.map((digit, index) => (
                  <Input
                    key={index}
                    ref={index === 0 ? firstInputRef : undefined}
                    id={`otp-${index}`}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]"
                    maxLength={1}
                    className="w-12 h-12 text-center text-2xl"
                    value={digit}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    autoComplete="off"
                  />
                ))}
              </div>
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/40" disabled={loading}>
              {loading ? "Verifying..." : <><LogIn className="mr-2 h-4 w-4" /> Verify & Login</>}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="flex justify-center">
          <Button variant="outline" onClick={handleResendCode} disabled={resending}>
            {resending ? "Sending..." : <><RefreshCw className="mr-2 h-4 w-4" /> Resend Code</>}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
